import jsPDF from "jspdf";
import html2canvas from "html2canvas";

/* ──────── CSV ──────── */
function csvEscape(v) {
  const s = String(v ?? "");
  return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function exportReportCSV(report, lang = "ru") {
  const L = {
    ru: { period: "Период", revenue: "Выручка (EUR)", txCount: "Транзакции", avgTicket: "Средний чек", refund: "Возвраты (%)", newClients: "Новых клиентов",
          daily: "По дням", date: "Дата", dailyRev: "Выручка", dailyTx: "Кол-во tx",
          customers: "Топ клиентов", name: "Имя", country: "Страна", orders: "Заказы", spent: "Потрачено (EUR)",
          transactions: "Транзакции", id: "ID", type: "Тип", amount: "Сумма (EUR)", customer: "Клиент", hash: "Hash", status: "Статус" },
    en: { period: "Period", revenue: "Revenue (EUR)", txCount: "Transactions", avgTicket: "Avg ticket", refund: "Refund rate (%)", newClients: "New clients",
          daily: "Daily breakdown", date: "Date", dailyRev: "Revenue", dailyTx: "Tx count",
          customers: "Top customers", name: "Name", country: "Country", orders: "Orders", spent: "Spent (EUR)",
          transactions: "Transactions", id: "ID", type: "Type", amount: "Amount (EUR)", customer: "Customer", hash: "Hash", status: "Status" },
  }[lang] || {};

  const parts = [];
  parts.push("# TGC Business Report");
  parts.push(`# ${L.period}: ${report.periodStart} → ${report.periodEnd} (${report.periodDays} days)`);
  parts.push("");
  parts.push(`${L.revenue},${report.totalRevenue}`);
  parts.push(`${L.txCount},${report.totalTxCount}`);
  parts.push(`${L.avgTicket},${report.avgTicket}`);
  parts.push(`${L.refund},${report.refundRate}`);
  parts.push(`${L.newClients},${report.newClients}`);
  parts.push("");

  parts.push(`# ${L.daily}`);
  parts.push([L.date, L.dailyRev, L.dailyTx].map(csvEscape).join(","));
  report.daily.forEach((d) => parts.push([d.date, d.revenue, d.txCount].join(",")));
  parts.push("");

  parts.push(`# ${L.customers}`);
  parts.push([L.name, L.country, L.orders, L.spent].map(csvEscape).join(","));
  report.topCustomers.forEach((c) => parts.push([csvEscape(c.name), csvEscape(c.country), c.orders, c.spent].join(",")));
  parts.push("");

  parts.push(`# ${L.transactions}`);
  parts.push([L.id, L.date, L.type, L.amount, L.customer, L.hash, L.status].map(csvEscape).join(","));
  report.transactions.forEach((t) => parts.push([t.id, csvEscape(t.date), csvEscape(t.type), t.amount, csvEscape(t.customer), t.hash, t.status].join(",")));

  // BOM for Excel UTF-8 detection
  const blob = new Blob(["\uFEFF" + parts.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `TGC_Report_${report.periodStart}_${report.periodEnd}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/* ──────── PDF (multi-page via html2canvas) ──────── */
const T = {
  ru: { title: "ОТЧЁТ БИЗНЕСА", subtitle: "Hybrid Global Economy · TradFi + DeFi", period: "Период", totals: "Итоги за период", revenue: "Выручка", txCount: "Транзакций", avgTicket: "Средний чек", refundRate: "Возвраты", newClients: "Новых клиентов", daily: "Выручка по дням", date: "Дата", dailyRev: "Выручка, €", dailyTx: "Tx", customers: "Топ-10 клиентов", name: "Имя", country: "Страна", orders: "Заказы", spent: "Потрачено, €", transactions: "Все транзакции", id: "ID", type: "Тип", amount: "Сумма, €", customer: "Клиент", hash: "Hash", status: "Статус", page: "Стр.", of: "из", footer: "Обработано через Turbo Global blockchain rails · ≈8 сек · 0.1-0.5%" },
  en: { title: "BUSINESS REPORT", subtitle: "Hybrid Global Economy · TradFi + DeFi", period: "Period", totals: "Totals", revenue: "Revenue", txCount: "Transactions", avgTicket: "Avg ticket", refundRate: "Refunds", newClients: "New clients", daily: "Daily revenue", date: "Date", dailyRev: "Revenue, €", dailyTx: "Tx", customers: "Top-10 customers", name: "Name", country: "Country", orders: "Orders", spent: "Spent, €", transactions: "All transactions", id: "ID", type: "Type", amount: "Amount, €", customer: "Customer", hash: "Hash", status: "Status", page: "Page", of: "of", footer: "Processed via Turbo Global blockchain rails · ~8 sec · 0.1-0.5%" },
  zh: { title: "业务报告", subtitle: "混合全球经济 · TradFi + DeFi", period: "期间", totals: "总计", revenue: "收入", txCount: "交易数", avgTicket: "平均客单", refundRate: "退款率", newClients: "新客户", daily: "每日收入", date: "日期", dailyRev: "收入, €", dailyTx: "交易", customers: "前10客户", name: "姓名", country: "国家", orders: "订单", spent: "消费, €", transactions: "全部交易", id: "ID", type: "类型", amount: "金额, €", customer: "客户", hash: "Hash", status: "状态", page: "页", of: "共", footer: "通过 Turbo Global 区块链处理 · 约 8 秒 · 0.1-0.5%" },
};

async function renderToCanvas(html) {
  const el = document.createElement("div");
  el.style.cssText = `position:fixed;left:-9999px;top:0;width:794px;background:#fff;color:#0a0a0a;font-family:-apple-system,"PingFang SC","Microsoft YaHei","Segoe UI","Noto Sans","Noto Sans SC",Arial,sans-serif;font-size:13px;line-height:1.55`;
  el.innerHTML = html;
  document.body.appendChild(el);
  try {
    return await html2canvas(el, { scale: 1.3, backgroundColor: "#FFFFFF", logging: false });
  } finally {
    document.body.removeChild(el);
  }
}

function headerHtml(t, report, pageNum, pagesTotal) {
  return `
    <div style="background:#050505;padding:22px 36px;color:#fff;display:flex;justify-content:space-between;align-items:flex-start">
      <div>
        <div style="font-size:22px;font-weight:900">TURBO <span style="color:#00F2FF">GLOBAL</span></div>
        <div style="color:#00F2FF;font-size:11px;margin-top:4px">${t.subtitle}</div>
      </div>
      <div style="text-align:right">
        <div style="color:#FFD700;font-weight:800;font-size:14px">${t.title}</div>
        <div style="color:#C8C8C8;font-size:10px;margin-top:4px">${t.period}: ${report.periodStart} → ${report.periodEnd}</div>
        <div style="color:#888;font-size:10px">${t.page} ${pageNum} ${t.of} ${pagesTotal}</div>
      </div>
    </div>
    <div style="height:3px;background:#00F2FF"></div>
  `;
}

function footerHtml(t) {
  return `<div style="padding:10px 36px 18px;border-top:1px solid #E5E5E5;color:#8A8A8A;font-size:9px">${t.footer}</div>`;
}

function kpiCard(label, value, color = "#00F2FF") {
  return `<div style="flex:1;padding:14px 16px;border:1px solid #eee;border-left:4px solid ${color};border-radius:8px;background:#FAFBFC">
    <div style="font-size:10px;color:#777;text-transform:uppercase;letter-spacing:1px">${label}</div>
    <div style="font-size:22px;font-weight:800;margin-top:4px">${value}</div>
  </div>`;
}

export async function exportReportPDF(report, lang = "ru") {
  const t = T[lang] || T.en;
  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();

  // pagination: page 1 — summary + daily, page 2 — customers, page 3+ — transactions (20/page)
  const perTxPage = 20;
  const txPages = Math.max(1, Math.ceil(report.transactions.length / perTxPage));
  const totalPages = 2 + txPages;

  const addCanvas = async (html, first = false) => {
    const canvas = await renderToCanvas(html);
    const imgH = (canvas.height * pageW) / canvas.width;
    if (!first) pdf.addPage();
    pdf.addImage(canvas.toDataURL("image/jpeg", 0.82), "JPEG", 0, 0, pageW, imgH);
  };

  /* ===== PAGE 1: Cover + totals + daily ===== */
  const page1 = headerHtml(t, report, 1, totalPages) + `
    <div style="padding:26px 36px">
      <div style="font-weight:700;font-size:15px;margin-bottom:12px">${t.totals}</div>
      <div style="display:flex;gap:10px;margin-bottom:24px">
        ${kpiCard(t.revenue,     `€${report.totalRevenue.toFixed(2)}`, "#00F2FF")}
        ${kpiCard(t.txCount,     report.totalTxCount,                  "#10F4A7")}
        ${kpiCard(t.avgTicket,   `€${report.avgTicket.toFixed(2)}`,    "#FFD700")}
      </div>
      <div style="display:flex;gap:10px;margin-bottom:28px">
        ${kpiCard(t.refundRate,  `${report.refundRate}%`,              "#FF3D6C")}
        ${kpiCard(t.newClients,  report.newClients,                    "#BC13FE")}
      </div>

      <div style="font-weight:700;font-size:14px;margin-bottom:10px">${t.daily}</div>
      <table style="width:100%;border-collapse:collapse;font-size:11px">
        <thead>
          <tr style="background:#F5F7FA;color:#555">
            <th style="text-align:left;padding:8px 10px">${t.date}</th>
            <th style="text-align:right;padding:8px 10px">${t.dailyRev}</th>
            <th style="text-align:right;padding:8px 10px">${t.dailyTx}</th>
          </tr>
        </thead>
        <tbody>
          ${report.daily.map((d) => `
            <tr style="border-bottom:1px solid #eee">
              <td style="padding:6px 10px">${d.date}</td>
              <td style="padding:6px 10px;text-align:right;font-weight:600">€${d.revenue.toFixed(2)}</td>
              <td style="padding:6px 10px;text-align:right">${d.txCount}</td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    </div>
    ${footerHtml(t)}
  `;
  await addCanvas(page1, true);

  /* ===== PAGE 2: Top customers ===== */
  const page2 = headerHtml(t, report, 2, totalPages) + `
    <div style="padding:26px 36px">
      <div style="font-weight:700;font-size:15px;margin-bottom:12px">${t.customers}</div>
      <table style="width:100%;border-collapse:collapse;font-size:12px">
        <thead>
          <tr style="background:#F5F7FA;color:#555">
            <th style="text-align:left;padding:10px">#</th>
            <th style="text-align:left;padding:10px">${t.name}</th>
            <th style="text-align:left;padding:10px">${t.country}</th>
            <th style="text-align:right;padding:10px">${t.orders}</th>
            <th style="text-align:right;padding:10px">${t.spent}</th>
          </tr>
        </thead>
        <tbody>
          ${report.topCustomers.map((c, i) => `
            <tr style="border-bottom:1px solid #eee">
              <td style="padding:8px 10px;color:#00B8C8;font-weight:700">${i + 1}</td>
              <td style="padding:8px 10px;font-weight:600">${c.name}</td>
              <td style="padding:8px 10px;color:#666">${c.country}</td>
              <td style="padding:8px 10px;text-align:right">${c.orders}</td>
              <td style="padding:8px 10px;text-align:right;font-weight:700;color:#B8860B">€${c.spent.toFixed(2)}</td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    </div>
    ${footerHtml(t)}
  `;
  await addCanvas(page2);

  /* ===== PAGE 3+: transactions (chunked) ===== */
  for (let p = 0; p < txPages; p++) {
    const chunk = report.transactions.slice(p * perTxPage, (p + 1) * perTxPage);
    const html = headerHtml(t, report, 3 + p, totalPages) + `
      <div style="padding:26px 36px">
        <div style="font-weight:700;font-size:15px;margin-bottom:12px">${t.transactions} · ${p * perTxPage + 1}–${p * perTxPage + chunk.length}</div>
        <table style="width:100%;border-collapse:collapse;font-size:10px">
          <thead>
            <tr style="background:#F5F7FA;color:#555">
              <th style="text-align:left;padding:8px 6px">${t.id}</th>
              <th style="text-align:left;padding:8px 6px">${t.date}</th>
              <th style="text-align:left;padding:8px 6px">${t.type}</th>
              <th style="text-align:right;padding:8px 6px">${t.amount}</th>
              <th style="text-align:left;padding:8px 6px">${t.customer}</th>
              <th style="text-align:left;padding:8px 6px">${t.hash}</th>
            </tr>
          </thead>
          <tbody>
            ${chunk.map((tx) => `
              <tr style="border-bottom:1px solid #eee">
                <td style="padding:6px;color:#00B8C8;font-family:monospace">${tx.id}</td>
                <td style="padding:6px">${tx.date}</td>
                <td style="padding:6px">${tx.type}</td>
                <td style="padding:6px;text-align:right;font-weight:700;color:${tx.amount < 0 ? "#d33" : "#127a4a"}">€${tx.amount.toFixed(2)}</td>
                <td style="padding:6px">${tx.customer}</td>
                <td style="padding:6px;font-family:monospace;color:#888">${tx.hash}</td>
              </tr>
            `).join("")}
          </tbody>
        </table>
      </div>
      ${footerHtml(t)}
    `;
    await addCanvas(html);
  }

  pdf.save(`TGC_Report_${report.periodStart}_${report.periodEnd}.pdf`);
}
